import React, { useState, useMemo, useEffect } from "react";
import { ArrowUpRight, Check, ClipboardList, Camera, Utensils, Wine, IceCream, Building2, Film, Phone, Mail, MapPin, User, Store } from "lucide-react";
import Photo from "./Photo";
import photos from "../../data/photography.json";
import { getCtaVariant } from "../../lib/ab";
import FormConsent from "./FormConsent";

// Tiered pricing — confirmed:
//   15–20 images: $30/image  (minimum 15 images required, minimum spend ~$450)
//   21–99 images: $25/image
//   100+ images: $10/image  (volume tier, requires $1000+ spend)
const PRICING = {
  minDishes: 15,
  tierLow: { max: 20, rate: 30 },     // 15–20 images
  tierMid: { max: 99, rate: 25 },     // 21–99 images
  tierVolume: { rate: 10 },           // 100+ images (volume)
  drinkMultiplier: 0.35,
  dessertMultiplier: 0.25,
  venueAddon: 450,
  reelsAddon: 650,
};

const rateFor = (count) => {
  if (count <= PRICING.tierLow.max) return PRICING.tierLow.rate;
  if (count <= PRICING.tierMid.max) return PRICING.tierMid.rate;
  return PRICING.tierVolume.rate;
};

const CATEGORIES = [
  { id: "mains", label: "Mains", count: "" },
  { id: "starters", label: "Starters" },
  { id: "sides", label: "Sides & Naans" },
  { id: "specials", label: "Chef's Specials" },
];

// ---------- MAIN TOOL ----------
export const ShootPlanner = () => {
  const [step, setStep] = useState("inputs"); // inputs | capture | loading | results
  const [dishes, setDishes] = useState(20);
  const [cats, setCats] = useState(["mains", "starters"]);
  const [drinks, setDrinks] = useState(false);
  const [desserts, setDesserts] = useState(true);
  const [venue, setVenue] = useState(false);
  const [reels, setReels] = useState(false);
  const [lead, setLead] = useState({ name: "", restaurant: "", suburb: "", phone: "", email: "" });

  const totals = useMemo(() => {
    const safeDishes = Math.max(dishes, PRICING.minDishes);
    const rate = rateFor(safeDishes);
    const base = safeDishes * rate;
    const drinkLine = drinks ? Math.round(safeDishes * PRICING.drinkMultiplier) * rate : 0;
    const dessertLine = desserts ? Math.round(safeDishes * PRICING.dessertMultiplier) * rate : 0;
    const venueLine = venue ? PRICING.venueAddon : 0;
    const reelsLine = reels ? PRICING.reelsAddon : 0;
    const total = base + drinkLine + dessertLine + venueLine + reelsLine;

    let tierLabel = "Starter rate · $30/image";
    if (rate === 25) tierLabel = "Mid-volume · $25/image";
    if (rate === 10) tierLabel = "Volume rate · $10/image";

    let pkg = "Quick Refresh · Half-day on-location";
    if (safeDishes >= 21 && safeDishes < 60) pkg = "Menu Library · Full-day on-location";
    if (safeDishes >= 60) pkg = "Full Service Library · Multi-day on-location";
    if (venue || reels) pkg = "Editorial Production · On-location";

    let timeOnSite = "3–4 hours on-location";
    if (safeDishes >= 25) timeOnSite = "Full day · 7–8 hours on-location";
    if (safeDishes >= 60) timeOnSite = "Two days · split shoot";

    return { rate, tierLabel, base, drinkLine, dessertLine, venueLine, reelsLine, total, pkg, timeOnSite };
  }, [dishes, drinks, desserts, venue, reels]);

  // Live preview — pick photos based on selections
  const preview = useMemo(() => {
    const heroes = photos.filter(p => p.category === "hero").map(p => p.slug);
    const menus = photos.filter(p => p.category === "menu").map(p => p.slug);
    const picks = [];
    picks.push(heroes[Math.floor(dishes / 10) % heroes.length]);
    picks.push(menus[(dishes * 2) % menus.length]);
    if (desserts) picks.push("hero-gulab-jamun-00");
    else picks.push(menus[(dishes * 3) % menus.length]);
    if (venue) picks.push("hero-banquet-01");
    else picks.push(menus[(dishes * 5) % menus.length]);
    return picks.filter(Boolean);
  }, [dishes, desserts, venue]);

  const onEstimate = () => setStep("capture");

  const onSubmit = async (e) => {
    e.preventDefault();
    setStep("loading");
    // Fire-and-forget — backend stores the lead. UX continues regardless.
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const variant = getCtaVariant();
      await fetch(`${API}/api/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...lead,
          dishes: Math.max(dishes, PRICING.minDishes),
          drinks, desserts, venue, reels,
          estimate_total: totals.total,
          package: totals.pkg,
          rate: totals.rate,
          source: "shoot-planner",
          cta_variant: variant.id,
        }),
      });
    } catch (err) {
      // Silent — show results either way; we'll still get a phone call follow-up
      console.error("Enquiry submit failed", err);
    }
    setTimeout(() => setStep("results"), 1800);
  };

  return (
    <section
      id="planner"
      data-testid="shoot-planner"
      className="relative bg-bone overflow-hidden"
    >
      {/* Subtle warm texture */}
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 py-20 lg:py-28">
        {/* Section header — direct, conversion-focused */}
        <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-10 lg:mb-14 items-end">
          <div className="col-span-12 lg:col-span-7">
            <h2
              className="font-display tracking-editorial leading-[0.95]"
              style={{ fontSize: "clamp(2.5rem, 5.5vw, 5rem)" }}
            >
              How much will it cost to
              <br />
              <span className="italic font-light text-ember">shoot your menu?</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pb-3">
            <p className="text-char text-base md:text-lg leading-relaxed max-w-md">
              Drag the slider. Tick the add-ons. You&apos;ll see the price
              in three clicks — we hand-confirm everything on a quick call.
            </p>
            <p className="mt-5 text-xs uppercase tracking-wider2 text-ember font-bold">
              Free · No login · Reply within 1 business day
            </p>
          </div>
        </div>

        {/* The pad — bigger frame, dramatic shadow */}
        <div className="relative bg-[#FBF7F0] border-2 border-ink shadow-[0_50px_120px_-40px_rgba(14,14,12,0.45)]">
          {/* Pad header strip — taller, more prominent */}
          <div className="flex items-center justify-between gap-4 px-6 lg:px-12 py-6 border-b-2 border-ink bg-ink text-bone">
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
              <span className="font-sans font-bold text-lg md:text-xl tracking-tight">
                Shoot Estimator.
              </span>
              <span className="font-display italic text-sm md:text-base text-bone/75 tracking-editorial">
                Pricing guide for Melbourne restaurant &amp; cafe photography.
              </span>
            </div>
            <div className="flex items-center gap-2 text-bone/80 shrink-0">
              <span className="w-2 h-2 rounded-full bg-ember animate-pulse" />
              <span className="text-xs uppercase tracking-wider2 font-bold font-semibold">Live</span>
            </div>
          </div>

          {/* Step content */}
          {step === "inputs" && (
            <InputsStep
              dishes={dishes} setDishes={setDishes}
              cats={cats} setCats={setCats}
              drinks={drinks} setDrinks={setDrinks}
              desserts={desserts} setDesserts={setDesserts}
              venue={venue} setVenue={setVenue}
              reels={reels} setReels={setReels}
              preview={preview}
              onNext={onEstimate}
            />
          )}
          {step === "capture" && (
            <CaptureStep lead={lead} setLead={setLead} onBack={() => setStep("inputs")} onSubmit={onSubmit} />
          )}
          {step === "loading" && <LoadingStep />}
          {step === "results" && (
            <ResultsStep totals={totals} dishes={dishes} drinks={drinks} desserts={desserts} venue={venue} reels={reels} preview={preview} lead={lead} />
          )}
        </div>
      </div>
    </section>
  );
};

// ---------- STEP 1: INPUTS ----------
const InputsStep = ({ dishes, setDishes, cats, setCats, drinks, setDrinks, desserts, setDesserts, venue, setVenue, reels, setReels, preview, onNext }) => {
  const toggleCat = (id) =>
    setCats((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="grid grid-cols-12">
      {/* LEFT — the form pad */}
      <div className="col-span-12 lg:col-span-7 p-6 lg:p-12 space-y-12 border-r border-ink/10">
        {/* Q1 — dish count */}
        <Field number="01" label="How many dishes?" hint={`${dishes} images · min ${PRICING.minDishes}`}>
          <div className="space-y-4">
            <input
              type="range"
              min="15"
              max="120"
              step="1"
              value={dishes}
              onChange={(e) => setDishes(Number(e.target.value))}
              data-testid="dish-slider"
              className="planner-slider w-full"
            />
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-sans font-bold text-7xl md:text-8xl leading-none text-ink tracking-tight">
                {dishes}
              </span>
              <span className="text-xs uppercase tracking-wider2 font-bold text-char-mute">
                images
              </span>
              <span className="ml-auto text-sm uppercase tracking-wider2 font-bold text-ember font-semibold whitespace-nowrap">
                ${rateFor(dishes)}/image
              </span>
            </div>
            <div className="flex justify-between text-xs uppercase tracking-wider2 font-semibold text-char-mute">
              <span>15</span><span>20 · $25</span><span>60</span><span>100+ · $10</span>
            </div>
            <p className="font-mono text-[10px] tracking-wider2 text-char-mute leading-relaxed pt-2">
              <span className="text-ember">·</span> 15–20 images @ $30 &nbsp;·&nbsp; 21–99 @ $25 &nbsp;·&nbsp; 100+ @ $10 (min spend $1,000)
            </p>
          </div>
        </Field>

        {/* Q2 — categories */}
        <Field number="02" label="What's on the menu?" hint="Multi-select">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCat(c.id)}
                data-testid={`cat-${c.id}`}
                className={`px-4 py-2.5 text-[11px] uppercase tracking-wider2 border transition-all ${
                  cats.includes(c.id)
                    ? "border-ember bg-ember text-bone"
                    : "border-ink/20 text-char hover:border-ink"
                }`}
              >
                {cats.includes(c.id) && <Check size={11} className="inline mr-1.5" strokeWidth={2.5} />}
                {c.label}
              </button>
            ))}
          </div>
        </Field>

        {/* Q3 — toggles */}
        <Field number="03" label="Add-ons" hint="Tap to include">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10 border border-ink/15">
            <Toggle on={drinks} onChange={() => setDrinks((v) => !v)} icon={Wine} label="Cocktails & beverages" sub="On-location lighting" testid="toggle-drinks" />
            <Toggle on={desserts} onChange={() => setDesserts((v) => !v)} icon={IceCream} label="Desserts & sweets" sub="Macro details included" testid="toggle-desserts" />
            <Toggle on={venue} onChange={() => setVenue((v) => !v)} icon={Building2} label="Venue & ambience" sub="Interior + service shots" testid="toggle-venue" />
            <Toggle on={reels} onChange={() => setReels((v) => !v)} icon={Film} label="Vertical reels" sub="3 short-form videos" testid="toggle-reels" />
          </div>
        </Field>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-ink/10">
          <p className="text-xs uppercase tracking-wider2 font-bold text-char-mute">
            Step 1 of 3 · Estimate is hand-confirmed on a quick phone call
          </p>
          <button
            type="button"
            onClick={onNext}
            data-testid="get-estimate-btn"
            className="group inline-flex items-center justify-center gap-3 bg-ink hover:bg-ember text-bone font-sans uppercase tracking-wider2 font-medium text-xs px-8 py-4 transition-all duration-500"
          >
            <span>Get My Estimate</span>
            <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>

      {/* RIGHT — live preview */}
      <div className="col-span-12 lg:col-span-5 bg-ink text-bone p-6 lg:p-10">
        <div className="flex items-center justify-between mb-6">
          <p className="text-xs uppercase tracking-wider2 font-bold text-bone/60">
            Live preview · sample frames
          </p>
          <Camera size={14} strokeWidth={1.4} className="text-ember" />
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {preview.slice(0, 4).map((slug, i) => (
            <Photo
              key={`${slug}-${i}`}
              slug={slug}
              alt="Sample"
              ratio={i === 0 ? "4/5" : i === 3 ? "4/5" : "1/1"}
              sizes="25vw"
            />
          ))}
        </div>
        <div className="mt-8 pt-6 border-t border-bone/15 space-y-3">
          <Counter label="Dishes" value={dishes} />
          <Counter label="Add-ons" value={[drinks && "Drinks", desserts && "Desserts", venue && "Venue", reels && "Reels"].filter(Boolean).join(" · ") || "—"} />
        </div>
      </div>
    </div>
  );
};

// ---------- STEP 2: LEAD CAPTURE ----------
const CaptureStep = ({ lead, setLead, onBack, onSubmit }) => {
  const set = (k) => (e) => setLead((p) => ({ ...p, [k]: e.target.value }));
  const fields = [
    { k: "name", label: "Your name", icon: User, placeholder: "Full name" },
    { k: "restaurant", label: "Restaurant / Brand", icon: Store, placeholder: "e.g. Lune Croissanterie" },
    { k: "suburb", label: "Suburb", icon: MapPin, placeholder: "e.g. Footscray, Carlton, Dandenong" },
    { k: "phone", label: "Phone", icon: Phone, placeholder: "0400 000 000" },
    { k: "email", label: "Email", icon: Mail, placeholder: "you@venue.com.au" },
  ];

  return (
    <form onSubmit={onSubmit} data-testid="lead-form" className="grid grid-cols-12">
      <div className="col-span-12 lg:col-span-7 p-6 lg:p-12">
        <div className="flex items-center gap-3 mb-3">
          <span className="font-display italic text-2xl text-ember">02</span>
          <span className="text-xs uppercase tracking-wider2 font-bold text-char-mute">
            Brief contact · before we run the numbers
          </span>
        </div>
        <h3 className="font-display text-3xl md:text-4xl tracking-editorial leading-tight mb-2">
          Where do we send the
          <br /><span className="italic font-light text-ember">estimate?</span>
        </h3>
        <p className="text-sm text-char leading-relaxed max-w-md mb-10">
          We hand-confirm every quote — your details stay private. No
          newsletters, no chase calls, no AI follow-ups.
        </p>

        <div className="space-y-7">
          {fields.map((f) => (
            <label key={f.k} className="block">
              <div className="flex items-center gap-2 mb-2">
                <f.icon size={11} strokeWidth={1.6} className="text-char-mute" />
                <span className="text-xs uppercase tracking-wider2 font-bold text-char/70">{f.label}</span>
              </div>
              <input
                type={f.k === "email" ? "email" : f.k === "phone" ? "tel" : "text"}
                required
                value={lead[f.k]}
                onChange={set(f.k)}
                placeholder={f.placeholder}
                data-testid={`lead-${f.k}`}
                className="w-full bg-transparent border-b border-ink/15 focus:border-ember text-ink placeholder:text-char-mute/50 font-display text-2xl tracking-editorial pb-3 focus:outline-none transition-colors"
              />
            </label>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-12 pt-6 border-t border-ink/10">
          <button type="button" onClick={onBack} data-testid="back-btn" className="text-xs uppercase tracking-wider2 font-bold text-char-mute hover:text-ember transition-colors">
            ← Back to brief
          </button>
          <button type="submit" data-testid="submit-lead" className="group inline-flex items-center gap-3 bg-ember hover:bg-ember-deep text-bone font-sans uppercase tracking-wider2 font-medium text-xs px-8 py-4 transition-all duration-500">
            <span>Reveal My Estimate</span>
            <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
        <FormConsent className="mt-5" />
      </div>

      {/* Side panel */}
      <div className="col-span-12 lg:col-span-5 bg-ink text-bone p-6 lg:p-10 flex flex-col justify-between min-h-[400px]">
        <div>
          <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-6">
            What happens next
          </p>
          <ul className="space-y-5">
            {[
              ["A", "We review your brief"],
              ["B", "Hand-written quote in 24h"],
              ["C", "Shoot date locked in"],
            ].map(([k, v]) => (
              <li key={k} className="flex items-baseline gap-4">
                <span className="font-display italic text-3xl text-ember leading-none">{k}.</span>
                <span className="font-display text-xl tracking-editorial">{v}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs uppercase tracking-wider2 font-bold text-bone/40 leading-loose">
          Your details are never sold or shared. <br />
          Removed on request, no questions asked.
        </p>
      </div>
    </form>
  );
};

// ---------- STEP 3: LOADING ----------
const LoadingStep = () => {
  const [phase, setPhase] = useState(0);
  const phrases = [
    "Reviewing your menu requirements…",
    "Matching to recent shoots…",
    "Pulling lighting & timing notes…",
    "Pricing your estimate…",
  ];
  useEffect(() => {
    const id = setInterval(() => setPhase((p) => (p + 1) % phrases.length), 480);
    return () => clearInterval(id);
  }, []);
  return (
    <div data-testid="loading-step" className="bg-ink text-bone p-12 lg:p-24 min-h-[440px] flex flex-col items-center justify-center text-center">
      <div className="flex items-center justify-center gap-2 mb-10">
        <span className="w-1.5 h-1.5 rounded-full bg-ember animate-pulse" />
        <span className="w-1.5 h-1.5 rounded-full bg-ember animate-pulse" style={{ animationDelay: "0.2s" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-ember animate-pulse" style={{ animationDelay: "0.4s" }} />
      </div>
      <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-6">
        Working
      </p>
      <h3 className="font-display text-3xl md:text-5xl italic font-light tracking-editorial leading-tight max-w-2xl">
        {phrases[phase]}
      </h3>
    </div>
  );
};

// ---------- STEP 4: RESULTS ----------
const ResultsStep = ({ totals, dishes, drinks, desserts, venue, reels, preview, lead }) => {
  const safeDishes = Math.max(dishes, PRICING.minDishes);
  const lines = [
    { k: `${safeDishes} images × $${totals.rate}`, sub: totals.tierLabel, v: totals.base },
    drinks && { k: `Drinks (${Math.round(safeDishes * PRICING.drinkMultiplier)} shots)`, sub: `at $${totals.rate}/image`, v: totals.drinkLine },
    desserts && { k: `Desserts (${Math.round(safeDishes * PRICING.dessertMultiplier)} shots)`, sub: `at $${totals.rate}/image`, v: totals.dessertLine },
    venue && { k: "Venue & ambience set", sub: "Interior + service shots", v: totals.venueLine },
    reels && { k: "Vertical reels (×5)", sub: "Short-form video", v: totals.reelsLine },
  ].filter(Boolean);

  return (
    <div data-testid="results-step" className="grid grid-cols-12">
      <div className="col-span-12 lg:col-span-7 p-6 lg:p-12">
        <div className="flex items-center gap-3 mb-3">
          <span className="font-display italic text-2xl text-ember">03</span>
          <span className="text-xs uppercase tracking-wider2 font-bold text-char-mute">
            Your estimate · {lead.restaurant || "your venue"}
          </span>
        </div>
        <h3 className="font-display text-3xl md:text-4xl tracking-editorial leading-tight mb-1">
          {lead.name ? `${lead.name.split(" ")[0]} — here's the` : "Here's the"}
          <br /><span className="italic font-light text-ember">honest number.</span>
        </h3>
        <p className="text-sm text-char-mute leading-relaxed mt-3 mb-10 max-w-md">
          This is an indicative estimate only. Your final price is hand-confirmed
          on a quick phone call — and may shift based on prep, travel, and
          styling needs.
        </p>

        {/* Big number */}
        <div className="border-t border-b border-ink/15 py-8 mb-8">
          <p className="text-xs uppercase tracking-wider2 font-bold text-char-mute mb-3">
            Estimated investment · {totals.tierLabel}
          </p>
          <div className="flex items-baseline gap-3">
            <span className="font-sans font-bold tracking-tight leading-none text-ink" style={{ fontSize: "clamp(3.5rem, 8vw, 6rem)" }}>
              ${totals.total.toLocaleString()}
            </span>
            <span className="text-xs uppercase tracking-wider2 font-bold text-char-mute">
              AUD · ex GST
            </span>
          </div>
        </div>

        {/* Line items */}
        <div className="space-y-3 mb-8">
          {lines.map((l) => (
            <div key={l.k} className="flex justify-between items-baseline pb-2 border-b border-ink/10">
              <div>
                <p className="font-sans text-sm text-char">{l.k}</p>
                {l.sub && <p className="text-xs uppercase tracking-wider2 font-semibold text-char-mute mt-1">{l.sub}</p>}
              </div>
              <span className="font-sans font-bold text-lg tracking-tight">${l.v.toLocaleString()}</span>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="bg-ink/5 border-l-2 border-ember p-4 mb-10">
          <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-1">Disclaimer</p>
          <p className="text-xs text-char leading-relaxed">
            Estimate only. The final quote is confirmed via a short phone call —
            actual price may vary depending on dish prep, ingredients, location
            travel, and any additional styling required.
          </p>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button data-testid="request-quote" className="group inline-flex items-center justify-center gap-3 bg-ember hover:bg-ember-deep text-bone font-sans uppercase tracking-wider2 font-medium text-xs px-8 py-4 transition-all duration-500">
            <span>Request hand-written quote</span>
            <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button className="font-sans uppercase tracking-wider2 font-medium text-xs px-8 py-4 border border-ink/30 hover:border-ink hover:bg-ink hover:text-bone text-ink transition-all duration-500">
            Email me this estimate
          </button>
        </div>
      </div>

      {/* Recommendation panel */}
      <div className="col-span-12 lg:col-span-5 bg-ink text-bone p-6 lg:p-10">
        <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-6">
          Recommended package
        </p>
        <h4 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-3">
          {totals.pkg.split("·")[0]}
        </h4>
        <p className="font-display italic text-bone/70 mb-8">
          {totals.pkg.split("·")[1]?.trim() || ""}
        </p>

        <div className="grid grid-cols-2 gap-2 mb-8">
          {preview.slice(0, 4).map((slug, i) => (
            <Photo key={`r-${slug}-${i}`} slug={slug} alt="" ratio="1/1" sizes="20vw" />
          ))}
        </div>

        <div className="space-y-4 pt-6 border-t border-bone/15">
          <Counter label="Time on-site" value={totals.timeOnSite} dark />
          <Counter label="Files delivered" value="5–7 days" dark />
          <Counter label="Usage" value="Web · Print · Social" dark />
        </div>
      </div>
    </div>
  );
};

// ---------- HELPERS ----------
const Field = ({ number, label, hint, children }) => (
  <div data-testid={`field-${number}`}>
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-4 mb-5">
      <div className="flex items-baseline gap-4 min-w-0">
        <span className="font-display italic text-3xl text-ember leading-none shrink-0">{number}</span>
        <h4 className="font-display text-xl md:text-2xl tracking-editorial leading-tight">{label}</h4>
      </div>
      {hint && <span className="text-xs uppercase tracking-wider2 font-bold text-char-mute pl-12 sm:pl-0 shrink-0">{hint}</span>}
    </div>
    {children}
  </div>
);

const Toggle = ({ on, onChange, icon: Icon, label, sub, testid }) => (
  <button
    type="button"
    onClick={onChange}
    data-testid={testid}
    className={`text-left p-5 transition-colors ${on ? "bg-ink text-bone" : "bg-[#FBF7F0] hover:bg-bone-warm text-ink"}`}
  >
    <div className="flex items-start justify-between mb-3">
      <Icon size={18} strokeWidth={1.4} className={on ? "text-ember" : "text-char-mute"} />
      <span className={`text-xs uppercase tracking-wider2 font-bold ${on ? "text-ember" : "text-char-mute"}`}>
        {on ? "Included" : "Add"}
      </span>
    </div>
    <p className="font-display text-lg tracking-editorial leading-tight">{label}</p>
    <p className={`text-xs uppercase tracking-wider2 font-bold mt-2 ${on ? "text-bone/60" : "text-char-mute"}`}>{sub}</p>
  </button>
);

const Counter = ({ label, value, dark = true }) => {
  // Detect if value is numeric / contains digits → render in sans
  const isNumeric = typeof value === "number" || /\d/.test(String(value));
  return (
    <div className="flex justify-between items-baseline">
      <span className={`text-xs uppercase tracking-wider2 font-bold ${dark ? "text-bone/60" : "text-char-mute"}`}>{label}</span>
      <span className={`${isNumeric ? "font-sans font-semibold tracking-tight" : "font-display tracking-editorial"} text-base ${dark ? "text-bone" : "text-ink"}`}>{value}</span>
    </div>
  );
};

// ---------- SECONDARY TOOLS ----------
const SECONDARY = [
  {
    id: "audit",
    num: "T2",
    title: "Delivery App Photo Audit",
    desc: "We review your Uber Eats, DoorDash and Menulog listings and tell you which photos are killing conversion. Free, takes 4 minutes.",
    cta: "Request audit",
    icon: ClipboardList,
  },
  {
    id: "planner",
    num: "T3",
    title: "Menu Shoot Planner",
    desc: "A 12-step planner — shot list, prep schedule, surfaces, props, and timing. Send to your kitchen before the shoot.",
    cta: "Open the planner",
    icon: Utensils,
  },
  {
    id: "needs",
    num: "T4",
    title: "How many photos does your restaurant actually need?",
    desc: "Quick rule-of-thumb based on covers, channels and seasons. Most operators over-shoot once and under-shoot every quarter after.",
    cta: "Run the numbers",
    icon: Camera,
  },
];

const SecondaryTools = () => (
  <div className="relative bg-ink text-bone">
    <div className="mx-auto max-w-[1600px] px-6 lg:px-10 py-20 lg:py-28">
      <div className="flex items-end justify-between mb-12 lg:mb-16">
        <div>
          <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-3">
            Operator tools · For Melbourne hospitality
          </p>
          <h3 className="font-display tracking-editorial leading-tight" style={{ fontSize: "clamp(1.875rem, 4vw, 3rem)" }}>
            Three more things we built
            <br /><span className="italic font-light text-bone/70">for the kitchens we work with.</span>
          </h3>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-bone/10">
        {SECONDARY.map((t) => (
          <a
            key={t.id}
            href="#"
            data-testid={`tool-${t.id}`}
            className="group relative bg-ink p-8 lg:p-10 transition-colors hover:bg-[#161613]"
          >
            <div className="flex items-center justify-between mb-10">
              <span className="font-display italic text-3xl text-ember leading-none">{t.num}</span>
              <t.icon size={18} strokeWidth={1.4} className="text-bone/40 group-hover:text-ember transition-colors" />
            </div>
            <h4 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-4 max-w-xs">
              {t.title}
            </h4>
            <p className="text-sm text-bone/70 leading-relaxed mb-10 max-w-sm">
              {t.desc}
            </p>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wider2 font-bold text-ember group-hover:text-bone transition-colors">
              <span className="border-b border-ember/40 group-hover:border-bone pb-0.5">{t.cta}</span>
              <ArrowUpRight size={12} strokeWidth={1.6} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </a>
        ))}
      </div>
    </div>
  </div>
);

export default ShootPlanner;
